import { createContext, useContext } from 'react'
import type { CustomTheme } from '@/features/theme-editor/theme-model'
import type { ThemeOption } from '@/features/theme-editor/theme-runtime'
import type { TextScale } from '@/features/theme-editor/text-scale'

/** Base display mode — drives the CSS class and invariant-palette base. */
export type Theme = 'light' | 'dark' | 'black'

export interface SettingsContextValue {
  /** Base mode of the active theme (back-compat: many consumers read this). */
  theme: Theme
  /** Id of the active theme (built-in id, preset id, or custom id). */
  themeId: string
  /** Bumps on every theme or text-size apply so CSS-var readers (charts) recompute. */
  themeVersion: number
  /** Text size as a whole percent (90 | 100 | 110). */
  textScale: TextScale
  /** Everything selectable in the picker: built-ins + starters + custom. */
  themeOptions: ThemeOption[]
  /** The user's saved custom themes. */
  customThemes: CustomTheme[]

  /** Cycle the three built-in Edge themes (back-compat). */
  toggleTheme: () => void
  /** Select a built-in by mode (back-compat). */
  setTheme: (theme: Theme) => void
  /** Select any theme by id. */
  selectTheme: (id: string) => void
  /** Create/update a custom theme (persists) and activate it. */
  saveCustomTheme: (theme: CustomTheme) => void
  /** Delete a custom theme; falls back to its base mode if it was active. */
  deleteCustomTheme: (id: string) => void
  /** Set the text size (persists, and bumps themeVersion so canvas text repaints). */
  setTextScale: (scale: TextScale) => void
}

export const SettingsContext = createContext<SettingsContextValue | null>(null)

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext)
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider')
  return ctx
}
